import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SetCopResponseStateMatchStatusCode,
  SetCopResponseStateName,
  SetCopResponseStateReason,
} from '@app/finxone-web-frontend/app/lib/actions/cop-response.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import {
  CheckPayeeResponse,
  Payee,
} from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  FormActionState,
  FormActionType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  accountNotSupported,
  accountNumberIncorrect,
  accountSwitched,
  invalidSecondaryIdentifier,
  noMatch,
  optedOut,
  sortCodeNotSupported,
} from '@finxone-platform/shared/default-templates';
import {
  ACCOUNTTYPE,
  APP_ZONES,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
  CheckPayeeMatchStatus,
  ProviderClass,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { OtpParam } from 'libs/shared/enums/common.enum';
import { Subject, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { setNoMatchReasonFromResponse } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-v2.utils';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-secondary-identifier',
  templateUrl: './secondary-identifier.component.html',
  styleUrls: ['./secondary-identifier.component.scss'],
})
export class SecondaryIdentifierComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public secondaryIdentifierForm: FormGroup = new FormGroup({});
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  private beneficiaryFlow: string;
  private formId: string;
  private formData: FormActionType;
  private srd: string;
  public submitButtonStyle: baseCssConfigStyle = {};
  public buttonContent = 'Continue';

  constructor(
    private fb: FormBuilder,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private route: ActivatedRoute,
    private store: Store,
    public formSubmissionService: FormSubmissionService,
    private router: Router,
    private configService: ConfigService,
    private accountService: AccountService,
  ) {
    super();
    if (!this.configService.getActiveConfigType().isWorkflow) {
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
        this.beneficiaryFlow = params['flow'];
      });
    }
  }
  ngOnInit() {
    this.formId =
      this.beneficiaryFlow === OtpParam.AddBeneficiary
        ? FormActionTypeEnum.ADD_BENEFICIARY
        : FormActionTypeEnum.EDIT_BENEFICIARY;
    this.store
      .select(FormActionState.getFormActionStateWithId(this.formId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((formData) => {
        if (formData) {
          this.formData = formData;
          this.srd = this.formData.formData['srd'];
        }
      });
    this.secondaryIdentifierForm = this.fb.group({
      secondaryIdentifier: [this.srd, Validators.required],
    });
    this.submitButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(this.dataIn, 'continueButton'),
      buttonNestedWidgetConfigStyles,
    );
    this.buttonContent =
      this.dataIn.widgetConfig?.nestedWidgets?.['continueButton'].widgetProperties.textContent ?? 'Continue';
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling'] ??
        false;
    }
  }

  public submitForm() {
    if (this.secondaryIdentifierForm.valid) {
      this.formData.formData['srd'] = this.secondaryIdentifierForm.value?.secondaryIdentifier;
      this.formSubmissionService.submitIndividualForm(this.formData.formData, '', this.formId);
      this.copCheck();
    }
  }

  private copCheck() {
    const formObject = this.formData.formData;
    if (formObject?.['sortCode']) {
      formObject['sortCode'] = formObject['sortCode'].replaceAll('-', '');
    }
    if (
      formObject?.['firstName'] &&
      formObject?.['lastName'] &&
      this.formData.formData['accountType'] === ACCOUNTTYPE.PERSONAL
    ) {
      // Add fullName under name key
      formObject['name'] = formObject['firstName'] + ' ' + formObject['lastName'];

      // Remove firstName and lastName from formObject
      delete formObject['firstName'];
      delete formObject['lastName'];
    }
    const payload = {
      name: formObject['name'],
      accountNumber: formObject['destinationIdentifier']['accountNumber'],
      sortCode: formObject['destinationIdentifier']['sortCode'],
      accountType: formObject['accountType'],
      srd: formObject['srd'],
    };
    this.accountService
      .checkPayee(payload as Payee, this.beneficiaryFlow)
      .subscribe((checkPayeeResponse: CheckPayeeResponse) => {
        this.store.dispatch(new SetCopResponseStateName(checkPayeeResponse.name));
        this.store.dispatch(new SetCopResponseStateMatchStatusCode(checkPayeeResponse.matchStatusCode));
        if (checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.MATCHED) {
          this.router.navigateByUrl(
            `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-match-cop?flow=${
              this.beneficiaryFlow
            }`,
          );
        } else if (
          checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.CLOSE_MATCH ||
          checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.NAME_MATCHED ||
          checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.ACCOUNT_TYPE_MISMATCH_NAME_CLOSE_MATCH
        ) {
          this.router.navigateByUrl(
            `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-close-match-cop?flow=${
              this.beneficiaryFlow
            }`,
          );
        } else {
          if (
            checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.INVALID_SECONDARY_IDENTIFIER &&
            !formObject.srd
          ) {
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(
                APP_ZONES.PAYMENT,
              )}/review-information-secondary-identifier-required-cop?flow=${this.beneficiaryFlow}`,
            );
          } else {
            setNoMatchReasonFromResponse(checkPayeeResponse.matchStatusCode, this.store);
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-no-match-cop?flow=${
                this.beneficiaryFlow
              }`,
            );
          }
        }
      });
  }
}
